/**
 * Image size options for different models
 */

import type { ImageModelValue, ImageSizeOption } from '../types'
import { NANO_BANANA_ASPECT_RATIOS, NS11_ASPECT_RATIOS } from './aspect-ratios'

/**
 * Get image size options based on selected model
 */
export function getImageSizeOptions(selectedModel: ImageModelValue): ImageSizeOption[] {
  switch (selectedModel) {
    case 'DALL_2':
      return [
        { label: 'Small (256x256)', value: 'small', ratio: '1:1', dimensions: '256x256' },
        { label: 'Medium (512x512)', value: 'medium', ratio: '1:1', dimensions: '512x512' },
        { label: 'Large (1024x1024)', value: 'large', ratio: '1:1', dimensions: '1024x1024' },
      ]

    case 'GPT_IMAGE_GENERATE':
    case 'GPT_IMAGE_EDIT':
      return [
        { label: 'Square (1024x1024)', value: 'square', ratio: '1:1', dimensions: '1024x1024' },
        { label: 'Portrait (1024x1536)', value: 'portrait', ratio: '2:3', dimensions: '1024x1536' },
        { label: 'Landscape (1536x1024)', value: 'landscape', ratio: '3:2', dimensions: '1536x1024' },
      ]

    case 'UNI_IMAGE':
      return NS11_ASPECT_RATIOS.map((item, index) => ({
        label: item.label,
        value: `ns11_${index}` as any,
        ratio: item.ratio.toString(),
        dimensions: `${item.width}x${item.height}`,
      }))

    case 'NANO_BANANA':
    case 'NANO_BANANA_PRO':
      return NANO_BANANA_ASPECT_RATIOS.map((item, index) => ({
        label: item.label,
        value: `nano_banana_${index}` as any,
        ratio: item.ratio.toString(),
        dimensions: `${item.width}x${item.height}`,
      }))

    case 'DALL_3_STANDARD':
    case 'DALL_3_HD':
    default:
      return [
        { label: 'Vertical (1024x1792)', value: 'vertical', ratio: '9:16', dimensions: '1024x1792' },
        { label: 'Square (1024x1024)', value: 'square', ratio: '1:1', dimensions: '1024x1024' },
        { label: 'Horizontal (1792x1024)', value: 'horizontal', ratio: '16:9', dimensions: '1792x1024' },
      ]
  }
}

/**
 * Get default image size for a model
 */
export function getDefaultImageSize(selectedModel: ImageModelValue): string {
  const options = getImageSizeOptions(selectedModel)
  return options[0]?.value || 'square'
}

/**
 * Get default aspect ratio index for NS11
 */
export function getDefaultNS11AspectRatioIndex(): number {
  // Default to 1:1 ratio (index 10)
  return NS11_ASPECT_RATIOS.findIndex(r => r.ratio === 1.0)
}

/**
 * Image size options for DALL-E 3 models (simple format for QuickGeneration)
 */
export const DALLE3_SIZE_OPTIONS = [
  { label: '1024x1024', value: 'square' },
  { label: '1024x1792', value: 'vertical' },
  { label: '1792x1024', value: 'horizontal' },
] as const

/**
 * Image size options for DALL-E 2 (simple format for QuickGeneration)
 */
export const DALLE2_SIZE_OPTIONS = [
  { label: '1024x1024', value: 'large' },
  { label: '512x512', value: 'medium' },
  { label: '256x256', value: 'small' },
] as const

/**
 * Image size options for GPT Image models (simple format for QuickGeneration)
 */
export const GPT_IMAGE_SIZE_OPTIONS = [
  { label: '1024x1024', value: 'square' },
  { label: '1024x1792', value: 'portrait' },
  { label: '1792x1024', value: 'landscape' },
] as const
